import { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import { Form, FormState } from 'informed';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from '@reach/router';
import { axisPaths } from '../AxisConstants';
import { AxisFormLoadingIndicator } from '../components/AxisFormLoadingIndicator';
import { AxisQuoteSubmissionProgress } from '../components/AxisQuoteSubmissionProgress';
import { useAxisQuoteSubmission } from '../../../hooks/useAxisQuoteSubmission';
import {
  AxisSubIndustryId,
  getContractorBusinessInternalName,
} from '../axisNewClientConstants';
import { useEnsureCalefyFormPresent } from '../../../hooks/useEnsureCalefyFormPresent';
import { withFormStyles, WithFormStyles } from '../../../util/withFormStyles';
import RenderedQuestionInstance from '../../common/QuoteComponents/RenderedQuestionInstance';
import {
  answerInstancesToFormState,
  formStateToAnswerInstances,
} from '../../QuoteWizard/QuoteForms/utility';
import { generateBusinessLinkUrl } from '../../BusinessLink/BusinessLink';
import { submitAxisInquiryQuote } from '../submitAxisInquiryQuote';
import { AxisSubmitAndExitFollowUpText } from '../components/AxisSubmitAndExitFollowUpText';
import QuoteWizardState, {
  updateErrors,
} from '../../../store/QuoteWizardState';
import { resetAxisState } from '../../../store/axisStore';
import { CompleteQuoteSubmissionError } from '../../QuoteWizard/ErrorHandling';
import { errorify } from '../../../util';
import { StoreState } from '../../../store';

export type NewClientFormSubmitAction = 'thanks' | 'continueApplication';

export type NewClientInquiryFormActionApi = {
  setSubmitAction: (action: NewClientFormSubmitAction) => void;
};

interface NewClientInquiryFormProps extends WithFormStyles {
  formInternalName: string;
  isContractorFlow: boolean;
  subIndustryId: AxisSubIndustryId | null;
  submissionStarted: boolean;
  setSubmissionStarted: (started: boolean) => void;
  onBack: () => void;
  onContinueApplication?: () => void;
  renderActions?: (api: NewClientInquiryFormActionApi) => ReactNode;
}

export const NewClientInquiryForm = withFormStyles()(
  ({
    formInternalName,
    isContractorFlow,
    subIndustryId,
    submissionStarted,
    setSubmissionStarted,
    onBack,
    onContinueApplication,
    renderActions,
    classes,
  }: NewClientInquiryFormProps) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const submitActionRef = useRef<NewClientFormSubmitAction>('thanks');
    const submissionError = useSelector(
      (state: StoreState) => state.quoteWizard.errors.completeQuoteSubmission,
    );
    const currentQuoteUUID = useSelector(
      (state: StoreState) => state.quoteWizard.currentQuoteUUID,
    );
    const formAnswers = useSelector(
      (state: StoreState) => state.quoteWizard.formAnswers,
    );
    const { form, loading } = useEnsureCalefyFormPresent({
      internalName: formInternalName,
    });

    const initialValues = useMemo(() => {
      if (!form) return undefined;
      const answeredForm = formAnswers[form.id];
      if (!answeredForm) return undefined;
      return answerInstancesToFormState(
        answeredForm.answers,
        form.questionInstances,
      );
    }, [form, formAnswers]);

    const handleSubmissionComplete = useCallback(() => {
      if (submitActionRef.current === 'continueApplication') {
        if (!subIndustryId) {
          navigate(axisPaths.welcome.url);
          return;
        }

        const businessInternalName =
          getContractorBusinessInternalName(subIndustryId);
        if (businessInternalName) {
          navigate(generateBusinessLinkUrl(businessInternalName));
          return;
        }

        navigate(axisPaths.welcome.url);
        return;
      }

      dispatch(QuoteWizardState.actions.resetSelections(['questionsPerPage']));
      dispatch(resetAxisState());
      navigate(axisPaths.thanks.url);
    }, [dispatch, navigate, subIndustryId]);

    const { taskPool } = useAxisQuoteSubmission({
      active: submissionStarted,
      onComplete: handleSubmissionComplete,
      resetStateOnComplete: () =>
        submitActionRef.current !== 'continueApplication',
    });

    useEffect(() => {
      const taskPoolData = taskPool.data?.getTaskPool;
      const error = taskPool.error || taskPoolData?.error;

      if (!submissionStarted || !error) {
        return;
      }

      setSubmissionStarted(false);
      dispatch(
        updateErrors({
          completeQuoteSubmission: errorify(error),
        }),
      );
    }, [
      dispatch,
      submissionStarted,
      setSubmissionStarted,
      taskPool.data?.getTaskPool,
      taskPool.error,
    ]);

    useEffect(() => {
      if (submissionStarted && submissionError) {
        setSubmissionStarted(false);
      }
    }, [submissionStarted, submissionError, setSubmissionStarted]);

    const handleSubmit = useCallback(
      (formState: FormState) => {
        if (!form || submissionStarted) {
          return;
        }

        if (
          submitActionRef.current === 'continueApplication' &&
          onContinueApplication
        ) {
          dispatch(
            QuoteWizardState.actions.submitAnswersForForm({
              form,
              answers: formStateToAnswerInstances(
                formState.values,
                form.questionInstances,
              ),
            }),
          );
          onContinueApplication();
          return;
        }

        setSubmissionStarted(true);
        submitAxisInquiryQuote(
          dispatch,
          form,
          formState,
          currentQuoteUUID,
          submitActionRef.current === 'continueApplication'
            ? 'IN_PROGRESS'
            : 'COMPLETE',
          formAnswers,
        );
      },
      [
        dispatch,
        form,
        submissionStarted,
        setSubmissionStarted,
        currentQuoteUUID,
        onContinueApplication,
        formAnswers,
      ],
    );

    const actionApi: NewClientInquiryFormActionApi = {
      setSubmitAction: (action) => {
        submitActionRef.current = action;
      },
    };

    return (
      <StepSection>
        {loading ? <AxisFormLoadingIndicator /> : null}
        {submissionStarted ? (
          <AxisQuoteSubmissionProgress
            taskPool={taskPool.data?.getTaskPool ?? null}
          />
        ) : null}
        {form && !submissionStarted ? (
          <FormSection>
            <Form
              autoComplete='off'
              allowEmptyStrings
              className={classes.form}
              initialValues={initialValues}
              onSubmit={handleSubmit}
            >
              {(form.questionInstances || []).map((questionInstance) => (
                <RenderedQuestionInstance
                  key={questionInstance.id}
                  questionInstance={questionInstance}
                  classes={classes}
                />
              ))}
              {renderActions ? (
                renderActions(actionApi)
              ) : isContractorFlow ? (
                <FormFooter>
                  <AxisSubmitAndExitFollowUpText />
                  <StepActions>
                    <SecondaryButton type='button' onClick={onBack}>
                      Back
                    </SecondaryButton>
                    <PrimaryButton
                      type='submit'
                      onClick={() => {
                        submitActionRef.current = 'continueApplication';
                      }}
                    >
                      Continue to Application
                    </PrimaryButton>
                    <SecondaryButton
                      type='submit'
                      onClick={() => {
                        submitActionRef.current = 'thanks';
                      }}
                    >
                      Submit and Exit Application
                    </SecondaryButton>
                  </StepActions>
                </FormFooter>
              ) : (
                <StepActions>
                  <SecondaryButton type='button' onClick={onBack}>
                    Back
                  </SecondaryButton>
                  <SubmitButton
                    type='submit'
                    onClick={() => {
                      submitActionRef.current = 'thanks';
                    }}
                  >
                    Submit
                  </SubmitButton>
                </StepActions>
              )}
            </Form>
          </FormSection>
        ) : null}
        <CompleteQuoteSubmissionError />
      </StepSection>
    );
  },
);

export const StepSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
}));

const FormSection = styled(Box)({
  textAlign: 'left',
});

export const FormFooter = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

export const StepActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: theme.spacing(2),
  marginTop: theme.spacing(1),
}));

export const PrimaryButton = styled(Button)(({ theme }) => ({
  ...theme.typography.button,
  color: theme.palette.primary.contrastText,
  backgroundColor: theme.palette.primary.main,
  padding: `${theme.spacing(0.75)} ${theme.spacing(2.5)}`,
  textTransform: 'none',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
  '&:disabled': {
    color: theme.palette.action.disabled,
    backgroundColor: theme.palette.action.disabledBackground,
  },
}));

export const SecondaryButton = styled(Button)(({ theme }) => ({
  ...theme.typography.button,
  color: theme.palette.primary.main,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: 'transparent',
  padding: `${theme.spacing(0.75)} ${theme.spacing(2.5)}`,
  textTransform: 'none',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.primary.main,
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  ...theme.typography.button,
  color: theme.palette.primary.contrastText,
  backgroundColor: theme.palette.primary.main,
  padding: `${theme.spacing(0.75)} ${theme.spacing(2)}`,
  textTransform: 'none',
  boxShadow: theme.shadows[2],
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    boxShadow: theme.shadows[4],
  },
}));
